import * as i0 from '@angular/core';
import { Injectable, Directive, Input, EventEmitter, Component, Output, ContentChildren, HostListener, HostBinding, NgModule } from '@angular/core';
import { pipe, BehaviorSubject, Subject, merge } from 'rxjs';
import { map, distinctUntilChanged, tap, switchMap, takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';

var Operators;
(function (Operators) {
    Operators["ADD"] = "+";
    Operators["AND"] = "&&";
    Operators["ABSOLUTE_VALUE"] = "abs";
    Operators["ASSIGN"] = "set";
    Operators["DATE"] = "Date";
    Operators["DIVIDE"] = "/";
    Operators["EQUAL"] = "==";
    Operators["GET"] = "get";
    Operators["GET_HASH_VALUE"] = "getHashValue";
    Operators["GREATER_THAN"] = ">";
    Operators["GREATER_THAN_OR_EQUAL"] = ">=";
    Operators["IF"] = "if";
    Operators["INCLUDES"] = "includes";
    Operators["LENGTH"] = "length";
    Operators["LESS_THAN"] = "<";
    Operators["LESS_THAN_OR_EQUAL"] = "<=";
    Operators["MATCH"] = "match";
    Operators["MODULO"] = "%";
    Operators["MULTIPLY"] = "*";
    Operators["NEW_DATE"] = "newDate";
    Operators["NOT"] = "!";
    Operators["NOT_EQUAL"] = "!=";
    Operators["OR"] = "||";
    Operators["PURE_ASSIGN"] = "=>";
    Operators["SUBTRACT"] = "-";
    Operators["SUM_COLUMN"] = "sumColumn";
    Operators["TERNARY"] = "?";
    Operators["GET_OBJECT_PROPERTY"] = "getObjectProperty";
    Operators["GET_STATE_PROPERTY"] = "getStateProperty";
    Operators["SET_FIELD_DISABLED"] = "setFieldDisabled";
    Operators["SET_FIELD_PROPERTY"] = "setFieldProperty";
    Operators["SET_FIELD_OPTIONS"] = "setFieldOptions";
    Operators["REMOVE_FIELD_OPTION"] = "removeFieldOption";
    Operators["ADD_FIELD_OPTIONS"] = "addFieldOptions";
    Operators["GET_FIELD_OPTIONS"] = "getFieldOptions";
    Operators["GET_FIELD_PROPERTY"] = "getFieldProperty";
    Operators["FETCH"] = "fetch";
    Operators["POPUP"] = "popup";
    Operators["ROUND"] = "round";
    Operators["LITERAL"] = "literal";
    Operators["SEARCH"] = "search";
    Operators["ACTIVATE"] = "activate";
})(Operators || (Operators = {}));
const dispatcher = (evaluatedArgs, state = {}, form) => ({
    [Operators.ADD]: () => evaluatedArgs.reduce((acc, val) => acc + val, 0),
    [Operators.AND]: () => evaluatedArgs.reduce((acc, val) => acc && val, true),
    [Operators.ABSOLUTE_VALUE]: () => Math.abs(evaluatedArgs[0]),
    [Operators.ASSIGN]: () => {
        state[evaluatedArgs[0]] = evaluatedArgs[1];
    },
    [Operators.DATE]: () => {
        const date = new Date(evaluatedArgs[0]);
        const method = evaluatedArgs[1];
        switch (method) {
            case 'getDate':
                return date.getDate();
            case 'getDay':
                return date.getDay();
            case 'getYear':
            case 'getFullYear':
                return date.getFullYear();
            case 'getHours':
                return date.getHours();
            case 'getMilliseconds':
                return date.getMilliseconds();
            case 'getMinutes':
                return date.getMinutes();
            case 'getMonth':
                return date.getMonth();
            case 'getSeconds':
                return date.getSeconds();
            case 'getTime':
                return date.getTime();
            case 'getTimezoneOffset':
                return date.getTimezoneOffset();
            case 'getUTCDate':
                return date.getUTCDate();
            case 'getUTCDay':
                return date.getUTCDay();
            case 'getUTCFullYear':
                return date.getUTCFullYear();
            case 'getUTCHours':
                return date.getUTCHours();
            case 'getUTCMilliseconds':
                return date.getUTCMilliseconds();
            case 'getUTCMinutes':
                return date.getUTCMinutes();
            case 'getUTCMonth':
                return date.getUTCMonth();
            case 'getUTCSeconds':
                return date.getUTCSeconds();
            default:
                return date;
        }
    },
    [Operators.DIVIDE]: () => evaluatedArgs.reduce((acc, val) => acc / val),
    [Operators.EQUAL]: () => {
        var _a, _b;
        const arg1 = ((_a = evaluatedArgs[0]) === null || _a === void 0 ? void 0 : _a.constructor) === Date
            ? evaluatedArgs[0].getTime()
            : evaluatedArgs[0];
        const arg2 = ((_b = evaluatedArgs[1]) === null || _b === void 0 ? void 0 : _b.constructor) === Date
            ? evaluatedArgs[1].getTime()
            : evaluatedArgs[1];
        return arg1 === arg2;
    },
    [Operators.GET]: () => {
        return state[evaluatedArgs[0]];
    },
    [Operators.GET_HASH_VALUE]: () => {
        const hash = evaluatedArgs[0];
        const key = evaluatedArgs[1];
        return hash === null || hash === void 0 ? void 0 : hash[key];
    },
    [Operators.GREATER_THAN]: () => {
        return evaluatedArgs[0] > evaluatedArgs[1];
    },
    [Operators.GREATER_THAN_OR_EQUAL]: () => evaluatedArgs[0] >= evaluatedArgs[1],
    [Operators.IF]: () => {
        if (evaluatedArgs[0]) {
            return evaluatedArgs[1];
        }
    },
    [Operators.INCLUDES]: () => {
        var _a, _b;
        return (_b = (_a = evaluatedArgs[0]) === null || _a === void 0 ? void 0 : _a.includes(evaluatedArgs[1])) !== null && _b !== void 0 ? _b : false;
    },
    [Operators.LENGTH]: () => { var _a, _b; return (_b = (_a = evaluatedArgs[0]) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0; },
    [Operators.LESS_THAN]: () => evaluatedArgs[0] < evaluatedArgs[1],
    [Operators.LESS_THAN_OR_EQUAL]: () => evaluatedArgs[0] <= evaluatedArgs[1],
    [Operators.MATCH]: () => {
        const regex = new RegExp(evaluatedArgs[0]);
        return regex.test(evaluatedArgs[1]);
    },
    [Operators.MODULO]: () => evaluatedArgs[0] % evaluatedArgs[1],
    [Operators.MULTIPLY]: () => evaluatedArgs.reduce((acc, val) => acc * val, 1),
    [Operators.NEW_DATE]: () => new Date(evaluatedArgs[0]),
    [Operators.NOT]: () => !evaluatedArgs[0],
    [Operators.NOT_EQUAL]: () => {
        var _a, _b;
        const arg1 = ((_a = evaluatedArgs[0]) === null || _a === void 0 ? void 0 : _a.constructor) === Date
            ? evaluatedArgs[0].getTime()
            : evaluatedArgs[0];
        const arg2 = ((_b = evaluatedArgs[1]) === null || _b === void 0 ? void 0 : _b.constructor) === Date
            ? evaluatedArgs[1].getTime()
            : evaluatedArgs[1];
        return arg1 !== arg2;
    },
    [Operators.OR]: () => evaluatedArgs.reduce((acc, val) => acc || val, false),
    [Operators.PURE_ASSIGN]: () => (Object.assign(Object.assign({}, state), { [evaluatedArgs[0]]: evaluatedArgs[1] })),
    [Operators.SUBTRACT]: () => evaluatedArgs.reduce((acc, val) => acc - val),
    [Operators.SUM_COLUMN]: () => {
        const targetArray = state[evaluatedArgs[0]];
        if (targetArray) {
            return targetArray.reduce((sum, row) => {
                if (typeof row[evaluatedArgs[1]] === 'number') {
                    return sum + row[evaluatedArgs[1]];
                }
                return sum;
            }, 0);
        }
        return 0;
    },
    [Operators.TERNARY]: () => {
        if (evaluatedArgs[0]) {
            return evaluatedArgs[1];
        }
        return evaluatedArgs[2];
    },
    [Operators.SET_FIELD_DISABLED]: () => {
        if (form) {
            form.setDisabled(evaluatedArgs[0], evaluatedArgs[1]);
        }
    },
    [Operators.SET_FIELD_PROPERTY]: () => {
        if (form) {
            form.setProperty(evaluatedArgs[0], evaluatedArgs[1], evaluatedArgs[2]);
        }
    },
    [Operators.GET_FIELD_OPTIONS]: () => {
        if (form) {
            return form.getOptions(evaluatedArgs[0]);
        }
        return [];
    },
    [Operators.SET_FIELD_OPTIONS]: () => {
        if (form) {
            form.setOptions(evaluatedArgs[0], evaluatedArgs[1]);
        }
    },
    [Operators.ADD_FIELD_OPTIONS]: () => {
        if (form) {
            form.addOptions(evaluatedArgs[0], evaluatedArgs[1]);
        }
    },
    [Operators.REMOVE_FIELD_OPTION]: () => {
        if (form) {
            form.removeOption(evaluatedArgs[0], evaluatedArgs[1]);
        }
    },
    [Operators.GET_FIELD_PROPERTY]: () => {
        if (form) {
            return form.getProperty(evaluatedArgs[0], evaluatedArgs[1]);
        }
        return undefined;
    },
    [Operators.GET_OBJECT_PROPERTY]: () => {
        var _a, _b;
        const obj = Object.assign({}, state);
        const pList = (_b = (_a = evaluatedArgs[0]) === null || _a === void 0 ? void 0 : _a.split('.')) !== null && _b !== void 0 ? _b : [];
        const key = pList.pop();
        const pointer = pList.reduce((accumulator, currentValue) => {
            return accumulator[currentValue];
        }, obj);
        return pointer[key];
    },
    [Operators.FETCH]: () => {
        if (form) {
            form.fetch.next({ endpoint: evaluatedArgs[0] });
        }
    },
    [Operators.POPUP]: () => {
        if (form) {
            form.popup.next(Object.assign({ endpoint: evaluatedArgs[0] }, evaluatedArgs[1] && { width: evaluatedArgs[1] }, evaluatedArgs[2] && { type: evaluatedArgs[2] }));
        }
    },
    [Operators.ROUND]: () => {
        return Math.round(evaluatedArgs[0]);
    },
    [Operators.LITERAL]: () => {
        return evaluatedArgs[0];
    },
    [Operators.SEARCH]: () => {
        if (form) {
            form.search.next(evaluatedArgs[0]);
        }
    },
    [Operators.ACTIVATE]: () => {
        form === null || form === void 0 ? void 0 : form.activate.next(evaluatedArgs[0]);
    },
});

function evaluateCommand(command, state = {}, form) {
    if ((command === null || command === void 0 ? void 0 : command.constructor) !== Object || command === null) {
        return command;
    }
    const operatorKey = Object.keys(command)[0];
    if (!Object.values(Operators).includes(operatorKey)) {
        return command;
    }
    const operator = operatorKey;
    let args = command[operator];
    if (args === undefined)
        return;
    // Evaluate each argument recursively if we're not evaluating a 'literal' command
    const evaluatedArgs = operator != Operators.LITERAL
        ? args.map((arg) => evaluateCommand(arg, state, form))
        : args;
    return dispatcher(evaluatedArgs, state, form)[operator]();
}

function statePluck(keys) {
    return pipe(map((state) => Object.assign({}, keys.reduce((total, key) => {
        var _a;
        return Object.assign(Object.assign({}, total), { [key]: (_a = state[key]) !== null && _a !== void 0 ? _a : null });
    }, {}))));
}
function stateQueryChange(key) {
    return pipe(statePluck(key), distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)));
}

const defaultFieldStatus$1 = {
    dirty: false,
    touched: false,
    valid: true,
    visited: false,
};
function isValidField(validators = [], form) {
    return validators.reduce((valid, { test }) => {
        if (typeof test === 'function') {
            return test(form) && valid;
        }
        return evaluateCommand(test, form) && valid;
    }, true);
}
function isValidForm(validators = {}, form) {
    return Object.values(validators).reduce((valid, fieldValidators) => {
        return isValidField(fieldValidators, form) && valid;
    }, true);
}
function useFieldsSchema(schemaDefinition) {
    const defaults = {
        componentProperties: {},
        defaultNull: '',
        defaultValue: null,
        disabled: false,
        display: true,
        horizontal: false,
        label: '',
        options: [],
        readonly: false,
        required: false,
        type: 'text',
        validations: [],
        information: undefined,
        append: null,
        group: null,
        showMessagesOnInit: false,
    };
    const requiredSchema = Object.entries(schemaDefinition).reduce((prev, [key, value]) => {
        const field = value;
        return Object.assign(Object.assign({}, prev), { [key]: Object.entries(defaults).reduce((prev, [k, v]) => {
                return field[k] === undefined ? Object.assign(Object.assign({}, prev), { [k]: v }) : prev;
            }, field) });
    }, {});
    return requiredSchema;
}
function defaultValuesFromSchema(schema, initialValues = {}) {
    return Object.entries(schema).reduce((prev, [name, field]) => {
        var _a, _b;
        const mergedValue = (_b = (_a = initialValues[name]) !== null && _a !== void 0 ? _a : field.defaultValue) !== null && _b !== void 0 ? _b : null;
        return (field === null || field === void 0 ? void 0 : field.type) !== 'button'
            ? Object.assign(Object.assign({}, prev), { [name]: mergedValue }) : Object.assign({}, prev);
    }, {});
}
function getFieldErrorMessages(validators, form) {
    return validators.reduce((errors, { test, message }) => {
        if (typeof test === 'function') {
            return test(form) ? errors : [...errors, message];
        }
        return evaluateCommand(test, form) ? errors : [...errors, message];
    }, []);
}
function getFormValidators(schema) {
    // messages need to be replaced with translatable labels
    return Object.entries(schema).reduce((formValidations, [field, fieldDefinition]) => {
        const { required, validations } = fieldDefinition;
        const defaultValidations = [
        // {
        //   test: ({ [field]: value }: any) => !/^\s+/.test(value),
        //   message: 'This field can not start with blank spaces',
        // },
        ];
        if (required) {
            defaultValidations.push({
                test: ({ [field]: value }) => checkValue(value),
                message: '',
            });
        }
        return Object.assign(Object.assign({}, formValidations), { [field]: [...defaultValidations, ...validations] });
    }, {});
}
function checkValue(value) {
    if (value === null || value === undefined)
        return false;
    switch (value.constructor) {
        case Object:
            return !!value && Object.keys(value).length > 0;
        case Array:
            return !!value && value.length > 0;
        case Number:
        case Boolean:
            return true;
        case Date:
        case String:
        default:
            return !!value;
    }
}
function isValidDate(date) {
    if (date === null || date === undefined) {
        return false;
    }
    return !isNaN(date) && date.constructor === Date;
}

class PerForm {
    constructor(formSchema, initialValues) {
        var _a;
        // this is not true default values these are the default values being over
        // written by the initial values
        this._defaultValues = {};
        this._controls = {};
        this._errors = new BehaviorSubject({});
        this._form = new BehaviorSubject({});
        this._labels = {};
        this._fieldSets = {};
        this._statuses = new BehaviorSubject({});
        this._effects = new BehaviorSubject([]);
        this._validators = {};
        this._valid = new BehaviorSubject(true);
        this._layout = {};
        this._tabSections = new BehaviorSubject({});
        this.tabSections = new BehaviorSubject([]);
        this.errors$ = this._errors.asObservable().pipe(tap((errors) => {
            this._valid.next(Object.values(errors).reduce((isValid, fieldErrors) => {
                return isValid && fieldErrors.length === 0;
            }, true));
        }));
        this.values$ = this._form.asObservable();
        this.statuses$ = this._statuses.asObservable();
        this.tabSections$ = this._tabSections.asObservable().pipe(map((hidden) => {
            return Object.values(hidden).reduce((hiddenSections, tabValues) => {
                return [...hiddenSections, ...tabValues];
            }, []);
        }), tap((hiddenSections) => this.tabSections.next(hiddenSections)));
        this.fetch = new Subject();
        this.popup = new Subject();
        this.search = new Subject();
        this.activate = new BehaviorSubject(null);
        this.showMessagesOnInit = false;
        this.contextMenu = null;
        this._schema = formSchema.fields;
        this._layout = formSchema.layout;
        this._controls = formSchema.controls;
        this.effects = formSchema.effects;
        this.showMessagesOnInit = formSchema.showMessagesOnInit;
        this.contextMenu = (_a = formSchema === null || formSchema === void 0 ? void 0 : formSchema.contextMenu) !== null && _a !== void 0 ? _a : null;
        this._initialValues = initialValues;
        this._initialize();
        this._setupEffects();
        this.tabSections$.subscribe();
        this.errors$.subscribe();
    }
    get defaultValues() {
        return this._defaultValues;
    }
    get controls() {
        return this._controls;
    }
    get errors() {
        return this._errors.value;
    }
    get labels() {
        return this._labels;
    }
    get fieldSets() {
        return this._fieldSets;
    }
    get schema() {
        return this._schema;
    }
    get statuses() {
        return this._statuses.value;
    }
    get values() {
        return this._form.value;
    }
    get layout() {
        return this._layout;
    }
    get effects() {
        return this._effects.value;
    }
    set effects(v) {
        this._effects.next(v);
    }
    get hiddenSections() {
        return this.tabSections.value;
    }
    getOptions(field) {
        var _a;
        return (_a = this._schema[field]) === null || _a === void 0 ? void 0 : _a.options;
    }
    setOptions(field, values) {
        this.schema[field] = Object.assign(Object.assign({}, this.schema[field]), { options: values });
    }
    removeOption(field, value) {
        this.schema[field] = Object.assign(Object.assign({}, this.schema[field]), { options: (this._schema[field].options = [
                ...this._schema[field].options.filter(({ value: optionValue }) => optionValue !== value),
            ]) });
    }
    addOptions(field, values) {
        this.schema[field] = Object.assign(Object.assign({}, this.schema[field]), { options: [...this.schema[field].options, ...values] });
    }
    getStatus(field) {
        return this._statuses.value[field];
    }
    get valid() {
        return this._valid.value;
    }
    isValidForm(form) {
        return isValidForm(this._validators, form);
    }
    patch(form) {
        Object.entries(form).forEach(([field, value]) => this.setField({ field, value }));
    }
    setDisabled(fieldName, value) {
        this._schema[fieldName] = Object.assign(Object.assign({}, this._schema[fieldName]), { disabled: value });
    }
    getProperty(fieldName, property) {
        return this._schema[fieldName][property];
    }
    setProperty(fieldName, property, value) {
        this._schema[fieldName] = Object.assign(Object.assign({}, this._schema[fieldName]), { [property]: value });
    }
    setField({ field, value, }) {
        const currentForm = this._form.value;
        this._form.next(Object.assign(Object.assign({}, currentForm), { [field]: value }));
        this.validateForm();
    }
    setStatus(field, property, status) {
        const fieldStatus = this._statuses.value[field];
        this._statuses.next(Object.assign(Object.assign({}, this._statuses.value), { [field]: Object.assign(Object.assign({}, fieldStatus), { [property]: status }) }));
    }
    addValidation(field, validation) {
        this.schema[field].validations = [
            ...this.schema[field].validations,
            validation,
        ];
        this._initializeValidators();
        this.validateForm();
    }
    validateField(field) {
        var _a;
        const errors = getFieldErrorMessages((_a = this._validators[field]) !== null && _a !== void 0 ? _a : [], this._form.value);
        this.setStatus(field, 'valid', errors.length === 0);
        this._errors.next(Object.assign(Object.assign({}, this._errors.value), { [field]: errors }));
    }
    validateForm() {
        Object.keys(this._schema).forEach((key) => this.validateField(key));
    }
    _initialize() {
        this._initializeValidators();
        this._initializeErrors();
        this._initializeLabels();
        this._initializeFieldSets();
        this._initializeStatuses();
        this._initializeForm();
    }
    _initializeErrors() {
        this._errors.next(Object.keys(this._schema).reduce((formErrors, field) => {
            return Object.assign(Object.assign({}, formErrors), { [field]: [] });
        }, {}));
    }
    _initializeLabels() {
        this._labels = Object.entries(this._schema).reduce((labels, [fieldName, field]) => {
            return Object.assign(Object.assign({}, labels), { [fieldName]: field.label });
        }, {});
    }
    _initializeFieldSets() {
        this._fieldSets = Object.entries(this._schema)
            .filter(([_, { fieldSet }]) => fieldSet) // filter out entries without fieldSet
            .reduce((fieldSets, [fieldKey, { fieldSet, fieldSetLabel }]) => {
            const currentFieldSet = fieldSets[fieldSet] || { fields: [] };
            return Object.assign(Object.assign({}, fieldSets), { [fieldSet]: Object.assign(Object.assign(Object.assign({}, currentFieldSet), { fields: [...currentFieldSet.fields, fieldKey] }), (fieldSetLabel && !currentFieldSet.label
                    ? { label: fieldSetLabel }
                    : {})) });
        }, {});
    }
    _initializeForm() {
        this._defaultValues = defaultValuesFromSchema(this._schema, this._initialValues);
        this.patch(this._defaultValues);
    }
    _initializeStatuses() {
        this._statuses.next(Object.keys(this._schema).reduce((formErrors, field) => {
            return Object.assign(Object.assign({}, formErrors), { [field]: defaultFieldStatus$1 });
        }, {}));
    }
    _initializeValidators() {
        this._validators = getFormValidators(this._schema);
    }
    updateSections(field, hiddenSections) {
        this._tabSections.next(Object.assign(Object.assign({}, this._tabSections.value), { [field]: [...hiddenSections] }));
    }
    _setupEffects() {
        const pipes = this._effects
            .pipe(switchMap((effectsDef) => merge(...Object.values(effectsDef).map((effect) => this.values$.pipe(stateQueryChange(effect.fields), tap(() => {
            (effect === null || effect === void 0 ? void 0 : effect.condition)
                ? evaluateCommand(effect.condition, this._form.value, this) === true &&
                    effect.actions.forEach((action) => evaluateCommand(action, this._form.value, this))
                : effect.actions.forEach((action) => evaluateCommand(action, this._form.value, this));
        }))))))
            .subscribe();
    }
}

class UseFormService {
    constructor() {
        this._form = new PerForm({
            fields: {},
            layout: {},
            controls: {},
            effects: [],
            showMessagesOnInit: false,
        }, {});
    }
    get form() {
        return this._form;
    }
    set form(form) {
        this._form = form;
    }
}
UseFormService.ɵprov = i0.ɵɵdefineInjectable({ factory: function UseFormService_Factory() { return new UseFormService(); }, token: UseFormService, providedIn: "root" });
UseFormService.decorators = [
    { type: Injectable, args: [{
                providedIn: 'root',
            },] }
];
UseFormService.ctorParameters = () => [];

class FieldsetDirective {
    constructor(useForm) {
        this.useForm = useForm;
        this.initialState = {};
    }
    ngOnInit() {
        this.useForm.form = this.pfFieldset;
    }
    save() {
        return this.useForm.form.values;
    }
    reset() {
        return this.useForm.form.patch(this.useForm.form.defaultValues);
    }
}
FieldsetDirective.decorators = [
    { type: Directive, args: [{
                selector: '[pfFieldset]',
            },] }
];
FieldsetDirective.ctorParameters = () => [
    { type: UseFormService }
];
FieldsetDirective.propDecorators = {
    pfFieldset: [{ type: Input }],
    initialState: [{ type: Input }],
    form: [{ type: Input }]
};

/* eslint-disable @angular-eslint/component-selector */
class PerFormComponent {
    constructor() {
        this.values = new EventEmitter();
    }
    onSave() {
        this.values.emit(this.save());
    }
    onReset() {
        this.reset();
    }
    save() {
        return this.fieldSets.reduce((form, fieldSet) => {
            return Object.assign(Object.assign({}, form), fieldSet.save());
        }, {});
    }
    reset() {
        return this.fieldSets.forEach((fieldSet) => fieldSet.reset());
    }
}
PerFormComponent.decorators = [
    { type: Component, args: [{
                selector: '[pfForm]',
                template: "<!-- should be able to use normal content selection here! -->\r\n<ng-content></ng-content>\r\n",
                styles: [""]
            },] }
];
PerFormComponent.propDecorators = {
    values: [{ type: Output }],
    fieldSets: [{ type: ContentChildren, args: [FieldsetDirective,] }],
    onSave: [{ type: HostListener, args: ['formSave',] }],
    onReset: [{ type: HostListener, args: ['formReset',] }]
};

const defaultFieldStatus = {
    dirty: false,
    touched: false,
    valid: true,
    visited: false,
};
// should be able to use the status in more clever way
// if this were a numeric enum we could set the first property
// to pristine and then use FieldStatus > 0 as the condition
// under which to show an error. If this were the case 'valid'
// doesn't belong here. So maybe we need something else entirely
var FieldStatusProperties;
(function (FieldStatusProperties) {
    FieldStatusProperties["DIRTY"] = "dirty";
    FieldStatusProperties["TOUCHED"] = "touched";
    FieldStatusProperties["VALID"] = "valid";
    FieldStatusProperties["VISITED"] = "visited";
})(FieldStatusProperties || (FieldStatusProperties = {}));
var FormControlRole;
(function (FormControlRole) {
    FormControlRole["PRIMARY"] = "primary";
    FormControlRole["SECONDARY"] = "secondary";
    FormControlRole["TERTIARY"] = "tertiary";
})(FormControlRole || (FormControlRole = {}));
var SectionDirection;
(function (SectionDirection) {
    SectionDirection["VERTICAL"] = "vertical";
    SectionDirection["HORIZONTAL"] = "horizontal";
})(SectionDirection || (SectionDirection = {}));

class ControlDirective {
    constructor(useForm) {
        this.useForm = useForm;
        this._shouldDestroy = new Subject();
    }
    get value() {
        return this._value;
    }
    focus() {
        this.handleFocus();
    }
    blur() {
        this.handleBlur();
    }
    input(input) {
        this.handleInput(input.value);
    }
    ngOnInit() {
        this.useForm.form.values$
            .pipe(tap(({ [this.pfControl]: value }) => {
            this._value = value;
        }, takeUntil(this._shouldDestroy)))
            .subscribe();
    }
    ngOnDestroy() {
        this._shouldDestroy.next(true);
    }
    handleInput(value) {
        this.useForm.form.setField({
            field: this.pfControl,
            value: value,
        });
        this.useForm.form.setStatus(this.pfControl, FieldStatusProperties.DIRTY, true);
    }
    handleBlur() {
        this.useForm.form.setStatus(this.pfControl, FieldStatusProperties.VISITED, true);
    }
    handleFocus() {
        this.useForm.form.setStatus(this.pfControl, FieldStatusProperties.TOUCHED, true);
    }
}
ControlDirective.decorators = [
    { type: Directive, args: [{
                selector: '[pfControl]',
            },] }
];
ControlDirective.ctorParameters = () => [
    { type: UseFormService }
];
ControlDirective.propDecorators = {
    pfControl: [{ type: Input }],
    value: [{ type: HostBinding, args: ['value',] }],
    focus: [{ type: HostListener, args: ['focus',] }],
    blur: [{ type: HostListener, args: ['blur',] }],
    input: [{ type: HostListener, args: ['input', ['$event.target'],] }]
};

class FeedbackDirective {
    constructor(useForm) {
        this.useForm = useForm;
        this.alwaysShow = false;
        this._shouldDestroy = new Subject();
    }
    get setClass() {
        return true;
    }
    get withError() {
        const fieldStatus = this.useForm.form.getStatus(this.pfFeedback);
        return fieldStatus.touched || fieldStatus.dirty || this.alwaysShow
            ? this._error
            : '';
    }
    ngOnInit() {
        this.useForm.form.errors$
            .pipe(tap(({ [this.pfFeedback]: errors }) => (this._error = errors[0])), takeUntil(this._shouldDestroy))
            .subscribe();
    }
    ngOnDestroy() {
        this._shouldDestroy.next(true);
    }
}
FeedbackDirective.decorators = [
    { type: Directive, args: [{
                selector: '[pfFeedback]',
            },] }
];
FeedbackDirective.ctorParameters = () => [
    { type: UseFormService }
];
FeedbackDirective.propDecorators = {
    pfFeedback: [{ type: Input }],
    alwaysShow: [{ type: Input }],
    setClass: [{ type: HostBinding, args: ['class.pf-feedback',] }],
    withError: [{ type: HostBinding, args: ['innerHTML',] }]
};

class FormDirective {
    constructor() {
        this.formSave = new EventEmitter();
        this.formReset = new EventEmitter();
    }
    onSubmit(event) {
        event.preventDefault();
        this.formSave.emit();
    }
    reset(event) {
        event.preventDefault();
        this.formReset.emit();
    }
}
FormDirective.decorators = [
    { type: Directive, args: [{
                selector: '[pfForm]',
                providers: [UseFormService],
            },] }
];
FormDirective.propDecorators = {
    formSave: [{ type: Output }],
    formReset: [{ type: Output }],
    onSubmit: [{ type: HostListener, args: ['submit', ['$event'],] }],
    reset: [{ type: HostListener, args: ['reset', ['$event'],] }]
};

class LabelDirective {
    constructor(useForm) {
        this.useForm = useForm;
    }
    get forAttribute() {
        return this.pfLabel;
    }
    get innerHTML() {
        var _a, _b;
        return (_b = (_a = this.useForm.form.schema[this.pfLabel]) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : '';
    }
}
LabelDirective.decorators = [
    { type: Directive, args: [{
                selector: '[pfLabel]',
            },] }
];
LabelDirective.ctorParameters = () => [
    { type: UseFormService }
];
LabelDirective.propDecorators = {
    pfLabel: [{ type: Input }],
    forAttribute: [{ type: HostBinding, args: ['for',] }],
    innerHTML: [{ type: HostBinding, args: ['innerHTML',] }]
};

class SaveDirective {
    constructor(useForm) {
        this.useForm = useForm;
        this._shouldDestroy = new Subject();
        this.save = new EventEmitter();
    }
    get withError() {
        return !this.useForm.form.valid;
    }
    onClick() {
        this.save.emit(this.useForm.form.values);
    }
    ngOnDestroy() {
        this._shouldDestroy.next(true);
    }
}
SaveDirective.decorators = [
    { type: Directive, args: [{
                selector: '[pfSave]',
            },] }
];
SaveDirective.ctorParameters = () => [
    { type: UseFormService }
];
SaveDirective.propDecorators = {
    save: [{ type: Output }],
    withError: [{ type: HostBinding, args: ['disabled',] }],
    onClick: [{ type: HostListener, args: ['click',] }]
};

class StatusDirective {
    constructor(useForm) {
        this.useForm = useForm;
        this._shouldDestroy = new Subject();
    }
    // This probably should not be here
    // It's convenient because we have the name
    // of the grid area in the right part of the structure
    // If we can avoid creating yet another directive that would be
    // great. Perhaps put this in the per-form.component
    get gridAreaName() {
        var _a;
        return ((_a = this.useForm.form.layout) === null || _a === void 0 ? void 0 : _a['grid-template-areas'])
            ? this.pfStatus
            : '';
    }
    get isDirty() {
        return this._status.dirty;
    }
    get isInvalid() {
        return !this._status.valid;
    }
    get isTouched() {
        return this._status.touched;
    }
    get isVisited() {
        return this._status.visited;
    }
    ngOnInit() {
        this.useForm.form.statuses$
            .pipe(tap(({ [this.pfStatus]: status }) => (this._status = status)), takeUntil(this._shouldDestroy))
            .subscribe();
    }
    ngOnDestroy() {
        this._shouldDestroy.next(true);
    }
}
StatusDirective.decorators = [
    { type: Directive, args: [{
                selector: '[pfStatus]',
            },] }
];
StatusDirective.ctorParameters = () => [
    { type: UseFormService }
];
StatusDirective.propDecorators = {
    pfStatus: [{ type: Input }],
    gridAreaName: [{ type: HostBinding, args: ['style.grid-area',] }],
    isDirty: [{ type: HostBinding, args: ['class.dirty',] }],
    isInvalid: [{ type: HostBinding, args: ['class.invalid',] }],
    isTouched: [{ type: HostBinding, args: ['class.touched',] }],
    isVisited: [{ type: HostBinding, args: ['class.visited',] }]
};

class AsteriskDirective {
    constructor(useForm) {
        this.useForm = useForm;
    }
    get asterisk() {
        return true;
    }
    get dirty() {
        var _a, _b;
        return (_b = (_a = this.useForm.form.statuses[this.pfAsterisk]) === null || _a === void 0 ? void 0 : _a.dirty) !== null && _b !== void 0 ? _b : false;
    }
    get touched() {
        var _a, _b;
        return (_b = (_a = this.useForm.form.statuses[this.pfAsterisk]) === null || _a === void 0 ? void 0 : _a.touched) !== null && _b !== void 0 ? _b : false;
    }
    get valid() {
        var _a, _b;
        return (_b = !((_a = this.useForm.form.statuses[this.pfAsterisk]) === null || _a === void 0 ? void 0 : _a.valid)) !== null && _b !== void 0 ? _b : false;
    }
    get visited() {
        var _a, _b;
        return (_b = (_a = this.useForm.form.statuses[this.pfAsterisk]) === null || _a === void 0 ? void 0 : _a.visited) !== null && _b !== void 0 ? _b : false;
    }
    get innerHTML() {
        var _a, _b, _c;
        return (((_a = this.useForm.form.schema[this.pfAsterisk]) === null || _a === void 0 ? void 0 : _a.required) ||
            !((_b = this.useForm.form.statuses[this.pfAsterisk]) === null || _b === void 0 ? void 0 : _b.valid)) &&
            !!((_c = this.useForm.form.schema[this.pfAsterisk]) === null || _c === void 0 ? void 0 : _c.label)
            ? '*'
            : '';
    }
}
AsteriskDirective.decorators = [
    { type: Directive, args: [{
                selector: '[pfAsterisk]',
            },] }
];
AsteriskDirective.ctorParameters = () => [
    { type: UseFormService }
];
AsteriskDirective.propDecorators = {
    asterisk: [{ type: HostBinding, args: ['class.lui-asterisk',] }],
    dirty: [{ type: HostBinding, args: ['class.dirty',] }],
    touched: [{ type: HostBinding, args: ['class.touched',] }],
    valid: [{ type: HostBinding, args: ['class.error',] }],
    visited: [{ type: HostBinding, args: ['class.visited',] }],
    pfAsterisk: [{ type: Input }],
    innerHTML: [{ type: HostBinding, args: ['innerHTML',] }]
};

class PerFormModule {
}
PerFormModule.decorators = [
    { type: NgModule, args: [{
                declarations: [
                    ControlDirective,
                    FeedbackDirective,
                    FieldsetDirective,
                    FormDirective,
                    LabelDirective,
                    PerFormComponent,
                    SaveDirective,
                    StatusDirective,
                    AsteriskDirective,
                ],
                imports: [CommonModule],
                exports: [
                    ControlDirective,
                    FeedbackDirective,
                    FieldsetDirective,
                    FormDirective,
                    LabelDirective,
                    PerFormComponent,
                    SaveDirective,
                    StatusDirective,
                    AsteriskDirective,
                ],
            },] }
];

const defaultLayout = {};
const defaultFieldLayout = {
    display: 'flex',
    'flex-direction': 'column',
    'row-gap': '0.25em',
};
// Take this out? I shouldn't be forcing the areas
function getDefaultGridTemplate(fields) {
    return fields.map((field) => `'${field}'`).join('\n');
}
function getDefaultFormLayout(layout, fields) {
    const gridTemplate = layout['grid-template-areas'] ||
        getDefaultGridTemplate(Object.keys(fields));
    return Object.assign(Object.assign({}, defaultLayout), layout);
}
function useFormSchema(formDefinition) {
    var _a, _b;
    const defaults = {
        effects: [],
        layout: {},
        controls: {},
    };
    const fields = useFieldsSchema(formDefinition.fields);
    const layout = getDefaultFormLayout(transformFormLayout(formDefinition.layout), fields);
    const controls = formDefinition.controls || defaults.controls;
    const effects = formDefinition.effects || defaults.effects;
    const showMessagesOnInit = (_a = formDefinition.showMessagesOnInit) !== null && _a !== void 0 ? _a : false;
    const contextMenu = (_b = formDefinition.contextMenu) !== null && _b !== void 0 ? _b : null;
    return {
        showMessagesOnInit,
        effects,
        layout,
        fields,
        controls,
        contextMenu,
    };
}
function transformFormLayout(layout) {
    return Object.assign({}, layout && Object.assign({}, layout), (layout === null || layout === void 0 ? void 0 : layout['grid-template-areas']) &&
        layout['grid-template-areas'].constructor === Array && {
        'grid-template-areas': `'${layout['grid-template-areas'].join(`' '`)}'`,
    });
}

class GenerateFormService {
    constructor() { }
    init(formDefinition, initialValues = {}) {
        return new PerForm(useFormSchema(formDefinition), initialValues);
    }
}
GenerateFormService.ɵprov = i0.ɵɵdefineInjectable({ factory: function GenerateFormService_Factory() { return new GenerateFormService(); }, token: GenerateFormService, providedIn: "root" });
GenerateFormService.decorators = [
    { type: Injectable, args: [{
                providedIn: 'root',
            },] }
];
GenerateFormService.ctorParameters = () => [];

/*
 * Public API Surface of per-form
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AsteriskDirective, ControlDirective, FeedbackDirective, FieldStatusProperties, FieldsetDirective, FormControlRole, FormDirective, GenerateFormService, LabelDirective, Operators, PerForm, PerFormComponent, PerFormModule, SaveDirective, SectionDirection, StatusDirective, UseFormService, defaultFieldStatus, dispatcher, evaluateCommand, statePluck, stateQueryChange };
//# sourceMappingURL=lbmx-per-form.js.map
